import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, throwError } from 'rxjs';
import { AppSettings } from '../app-settings';
import { CreateProductPayload, UpdateProductPayload } from '../interfaces/product';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  constructor(private http: HttpClient) { }

  // Los productos pertenecen a una compañía, así que el listado siempre va
  // acotado a ella: una compañía no puede ver los productos de otra.
  getProducts(companyId: number, filters?: { search?: string; id_season?: number; active?: boolean }) {
    const params: string[] = [];
    if (filters?.search) params.push(`search=${encodeURIComponent(filters.search)}`);
    if (filters?.id_season) params.push(`id_season=${filters.id_season}`);
    if (filters?.active !== undefined) params.push(`active=${filters.active}`);
    let url = `${AppSettings.API_URI_V2}/companies/${companyId}/products`;
    if (params.length) url += `?${params.join('&')}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  getProductById(companyId: number, productId: number) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/products/${productId}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * Productos de una temporada, acotables por categoría de abonado y por nivel
   * de butaca — las dos dimensiones de las que depende el producto. Puede
   * devolver más de uno para la misma combinación (con y sin carnet físico).
   *
   * Para saber qué puede pagar un abono CONCRETO hay que usar
   * `SeasonPassService.getAvailableProducts`, que resuelve el nivel de su
   * butaca en el servidor. Este método es para el panel, que sí conoce a qué
   * nivel está moviendo una butaca.
   *
   * `includeAdminOnly` pide además los productos de canal panel (invitaciones,
   * descuentos). El backend solo lo atiende si el usuario es staff de la
   * compañía dueña de la temporada, así que pedirlo desde una pantalla pública
   * no abriría nada — pero aun así solo debe pedirse desde el panel.
   */
  getProductsBySeason(seasonId: number, idCategory?: number, idLevel?: number, includeAdminOnly: boolean = false) {
    const params: string[] = [];
    if (idCategory) params.push(`id_category=${idCategory}`);
    if (idLevel) params.push(`id_level=${idLevel}`);
    if (includeAdminOnly) params.push('include_admin_only=1');
    let url = `${AppSettings.API_URI_V2}/seasons/${seasonId}/products`;
    if (params.length) url += `?${params.join('&')}`;
    return this.http.get<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  createProduct(companyId: number, payload: CreateProductPayload) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/products`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  updateProduct(companyId: number, productId: number, payload: UpdateProductPayload) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/products/${productId}`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }

  deleteProduct(companyId: number, productId: number) {
    const url = `${AppSettings.API_URI_V2}/companies/${companyId}/products/${productId}`;
    return this.http.delete<any>(url).pipe(
      map((data) => data),
      catchError(err => throwError(() => err))
    );
  }
}
